import { env } from './env';

/**
 * Static feature flags, resolved per environment.
 *
 * There is no remote flag provider yet, so these tables ARE the provider.
 * Three of them rather than one: a flag that is safe to show a tester on
 * staging is not automatically safe in front of paying users, and a flag
 * that is safe only on a dev's own machine (nothing verifies it belongs to
 * them) is not automatically safe on staging either — this repo is
 * trunk-based, so separate tables mean shipping all three at the same moment.
 *
 * Keys are kebab-case so they can later map 1:1 onto a provider's flag names,
 * and `isFeatureEnabled` takes the table as a parameter so a future provider
 * only has to hand over its resolved table.
 */
export type FeatureFlag = 'email-credentials-auth' | 'telegram-linking' | 'skip-email-verification';

export type FeatureFlagTable = Record<FeatureFlag, boolean>;

/** What real users get. Anything unfinished is off here. */
const PRODUCTION_FLAGS: FeatureFlagTable = {
  // Email + password sign-in / sign-up. Off ⇒ Login and Signup offer only the
  // social providers (Google, Facebook, LinkedIn).
  'email-credentials-auth': false,
  // Telegram account linking. Off ⇒ the connect card, the Channels section and
  // the setup step all disappear, and no /telegram/* request is made.
  'telegram-linking': true,
  // Skips the OTP check screen after sign-up/login. Off ⇒ every account must
  // verify its email, on staging and production alike.
  'skip-email-verification': false,
};

/**
 * What staging gets: the surfaces that are built but not yet cleared for
 * production. Email credentials are how a tester reaches an account without
 * going through a social provider. `skip-email-verification` stays OFF here —
 * staging still proves an account owns its inbox, same as production; testers
 * verify for real (or flip `emailVerified` in Mongo by hand when delivery is
 * down, see docs/ops).
 */
const STAGING_FLAGS: FeatureFlagTable = {
  'email-credentials-auth': true,
  'telegram-linking': true,
  'skip-email-verification': false,
};

/**
 * What a fully local API gets: everything staging gets, PLUS skipping email
 * verification — the backend already exempts local dev from
 * `requireEmailVerification` (see `auth-config.ts`), so this only removes the
 * FE's OWN separate `emailVerified` redirect, which otherwise still sends a
 * locally-signed-up user to a code they were never blocked from skipping.
 */
const LOCAL_DEV_FLAGS: FeatureFlagTable = {
  ...STAGING_FLAGS,
  'skip-email-verification': true,
};

/**
 * Which table a build gets, decided by the API it was pointed at.
 *
 * `VITE_API_URL` already differs per build (`api-staging.heylili.io` on staging
 * and normally in local dev, `api.heylili.io` in production, `localhost` when a
 * local dev API is running), so this needs no new environment variable and no
 * Cloudflare Pages dashboard change — one less thing to forget when a new
 * preview environment appears.
 *
 * Fails CLOSED: anything unrecognised, empty or undefined resolves to
 * production. A build whose env did not load must not quietly turn on the
 * surfaces production is not ready for.
 */
export function resolveFlags(apiUrl: string | undefined): FeatureFlagTable {
  // Copied, not aliased: tests flip entries on the resolved table, and handing
  // back the source object would let one of them rewrite it for the process.
  if (apiUrl?.includes('localhost')) return { ...LOCAL_DEV_FLAGS };
  if (apiUrl?.includes('api-staging.')) return { ...STAGING_FLAGS };
  return { ...PRODUCTION_FLAGS };
}

export const FEATURE_FLAGS: FeatureFlagTable = resolveFlags(env.API_URL);

/** True when `flag` is switched on in `flags` (defaults to the resolved table). */
export function isFeatureEnabled(
  flag: FeatureFlag,
  flags: Partial<FeatureFlagTable> = FEATURE_FLAGS
): boolean {
  return flags[flag] ?? false;
}
